import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, RelatedLinks, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { GarageOps, JuiceFSOps } from "../module/StorageOps";
import { StorageClusterView } from "../module/StorageCluster";
import { StorageSwitchJobs } from "./StorageSwitch";
import { useSearchParams } from "react-router";
import { Button, ConfirmDialog, Section, StateView, Tabs, TabsContent, TabsList, TabsTrigger, useAuth, useToast } from "@ozpanel/ui";

// Shared storage: the volumes, and what each tenant's home actually rests on.
//
// GlusterFS mounts 60 routes and v2 reached two of them (health and the volume list). Everything
// an operator does when storage goes wrong — heal, resolve a split brain, replace a brick,
// rebalance, extend a tenant volume onto a new node, remount a home that came off — could only be
// done over SSH.
//
// The interlocks this screen exists to show, all of them recorded in CLAUDE.md as things that
// looked fine and were not:
//
//   - MOUNTED IS NOT REPLICATED. A 1-brick Distribute volume reports fuse.glusterfs exactly like a
//     replica-2 one, so "mounted" proves the path works, not that the data exists twice. The brick
//     count and replica factor are the only answer, and they lead the table.
//   - A MOUNT THAT IS ABSENT is invisible to every "broken mount" check, because there is no broken
//     mount to find. A tenant home came off both nodes and Dovecot read the empty local directory
//     underneath while every health check stayed green.
//   - HEAL PENDING AND SPLIT BRAIN are the two states where every daemon reports itself healthy and
//     the data disagrees between copies.

interface Volume {
  id?: string;
  name: string;
  type?: string;
  status?: string;
  started?: boolean;
  brick_count?: number;
  bricks_online?: number;
  replica_count?: number;
  heal_pending?: number;
  split_brain?: number;
  bricks?: { host?: string; path?: string; online?: boolean }[] | null;
  status_read?: boolean;
  options?: Record<string, string> | null;
}

interface TenantVol {
  tenant: number | string;
  tenant_name?: string;
  volume?: string;
  type?: string;
  replica?: number;
  /**
   * The BRICKS THEMSELVES, from TenantStatus.Bricks ([]Brick) — not a count. `bricks_total` and
   * `bricks_online` are the counts, and this was declared as a third one, so the array was
   * rendered concatenated wherever it was touched. Three fields, two meanings, one of them
   * typed as the other.
   */
  bricks?: { name?: string; host?: string; path?: string; arbiter?: boolean; online?: boolean; port?: string }[] | null;
  bricks_total?: number;
  bricks_online?: number;
  data_bricks?: number;
  arbiters?: number;
  /**
   * A COUNT — TenantStatus.NodesHolding is an `int`. It was declared `string[] | null`, which is
   * the same mistake in the opposite direction and appears five times across this console:
   * typing a count as the collection it counts, or the collection as its count.
   */
  nodes_holding?: number;
  exists?: boolean;
  started?: boolean;
  mounted?: boolean;
  in_fstab?: boolean;
  mount_point?: string;
  mount_detail?: string;
  heal_pending?: number;
  split_brain?: number;
  ha?: boolean;
  brick_has_data?: boolean;
  home_has_local_data?: boolean;
  why?: string;
  /**
   * The placement that WOULD apply — TenantStatus.Plan is a *TenantVolume, an object, and it is
   * present precisely so a degraded volume can say what it is missing. Typed as a string it
   * would have thrown "Objects are not valid as a React child" the first time anyone rendered it.
   */
  plan?: {
    volume?: string;
    mount_point?: string;
    brick_path?: string;
    nodes?: string[] | null;
    replica?: number;
    /** WHICH NODE holds the arbiter brick — a name, not a flag. TenantVolume.Arbiter is a string. */
    arbiter?: string;
  } | null;
}

interface GlusterAccess {
  volume?: string;
  auth_allow?: string;
  auth_reject?: string;
  expected?: string;
  in_step?: boolean;
  note?: string;
}

interface TenantsView {
  items?: TenantVol[] | null;
  total?: number;
  node_count?: number;
  nodes?: string[] | null;
  ha?: number;
  at_risk?: number;
  /**
   * A BOOLEAN — whether any figure on this page could not be proven. It was declared `number`
   * and rendered in the counts row beside no_volume and not_mounted, which ARE counts, so the
   * page showed "unproven: true" where every neighbour was a number. A flag standing in a line
   * of counts is read as a count.
   */
  unproven?: boolean;
  no_volume?: number;
  not_mounted?: number;
  split_brain?: number;
  volumes_on_this_node?: number;
  density_ceiling?: number;
  note?: string;
}

const storageTabs = ["juicefs", "switch", "tenants", "volumes"];

export function StorageScreen() {
  // The tab and an opened switch job live in the URL (?tab=switch&job=N), so the JuiceFS engine card
  // and a started job's result can link straight to the job's progress.
  const [params, setParams] = useSearchParams();
  const tab = storageTabs.includes(params.get("tab") ?? "") ? (params.get("tab") as string) : "juicefs";
  const jobParam = Number(params.get("job"));
  const openJob = Number.isInteger(jobParam) && jobParam > 0 ? jobParam : null;
  const update = (next: Record<string, string | null>) =>
    setParams(
      (cur) => {
        const p = new URLSearchParams(cur);
        for (const [k, v] of Object.entries(next)) {
          if (v == null) p.delete(k);
          else p.set(k, v);
        }
        return p;
      },
      { replace: true },
    );
  return (
    <div>
      <PageHeader title="Shared storage" subtitle="JuiceFS volumes across the cluster, and Garage where their content lives" />
      <RelatedLinks
        inline
        links={[
          { label: "JuiceFS module", to: "/modules/juicefs", hint: "settings, service & usage per volume, configuration, notifications" },
          { label: "Garage module", to: "/modules/garage", hint: "service & usage, notifications" },
          { label: "JuiceFS log", to: "/modules/logs", hint: "the clients' log file (Log files section)" },
        ]}
      />
      <Tabs value={tab} onValueChange={(v) => update({ tab: v === "juicefs" ? null : v, job: null })}>
        <TabsList>
          {/* JuiceFS serves every shared path since the GlusterFS retirement (2026-09-23), so it opens
              first; the GlusterFS tabs remain for the retired volumes and an un-retire. */}
          <TabsTrigger value="juicefs">JuiceFS &amp; Garage</TabsTrigger>
          <TabsTrigger value="switch">Switch jobs</TabsTrigger>
          <TabsTrigger value="tenants">Tenant homes (GlusterFS, retired)</TabsTrigger>
          <TabsTrigger value="volumes">GlusterFS volumes (retired)</TabsTrigger>
        </TabsList>
        <TabsContent value="switch">
          <StorageSwitchJobs openJob={openJob} onOpenJob={(id) => update({ job: id == null ? null : String(id) })} />
        </TabsContent>
        <TabsContent value="tenants">
          <TenantsTab />
        </TabsContent>
        <TabsContent value="volumes">
          <VolumesTab />
        </TabsContent>
        <TabsContent value="juicefs">
          <div className="space-y-6">
            <StorageClusterView />
            <JuiceFSOps />
            <GarageOps />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function useRunner(keys: string[][]) {
  const qc = useQueryClient();
  const { notify } = useToast();
  return (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        for (const k of keys) void qc.invalidateQueries({ queryKey: k });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));
}

function TenantsTab() {
  const { client } = useAuth();
  const run = useRunner([["gluster-tenants"]]);
  // Who may mount, and whether it still matches the registry.
  const access = useQuery({
    queryKey: ["gluster-access"],
    queryFn: () => client.apiFetch<GlusterAccess>("/glusterfs/access"),
    retry: false,
  });

  const q = useQuery({
    queryKey: ["gluster-tenants"],
    queryFn: () => client.apiFetch<TenantsView>("/glusterfs/tenants"),
    retry: false,
  });
  const d = q.data ?? {};
  const [confirm, setConfirm] = React.useState<{ title: string; body: string; label: string; go: () => Promise<unknown> } | null>(null);
  const [inspecting, setInspecting] = React.useState<string | null>(null);

  const cols: DataListColumn<TenantVol>[] = [
    {
      key: "tenant", label: "Tenant", sortable: true, value: (t) => String(t.tenant_name ?? t.tenant),
      render: (t) => (
        <span>
          <span className="font-mono text-[12.5px]">{t.tenant_name ?? t.tenant}</span>
          {t.volume && <span className="ml-2 text-[11.5px] text-muted">{t.volume}</span>}
        </span>
      ),
    },
    {
      // Replication, stated as copies. "replica 2 + arbiter" is not something a reader should have
      // to reconstruct from a type string, and one copy is the fact that matters most.
      key: "copies", label: "Copies", sortable: true, value: (t) => t.data_bricks ?? 0,
      render: (t) => {
        const copies = t.data_bricks ?? t.replica ?? 0;
        const tone = copies >= 2 ? "ok" : copies === 1 ? "bad" : "warn";
        return (
          <span>
            <StatusPill tone={tone}>{copies === 1 ? "one copy" : `${copies} copies`}</StatusPill>
            {(t.arbiters ?? 0) > 0 && <span className="ml-2 text-[11.5px] text-muted">+{t.arbiters} arbiter</span>}
          </span>
        );
      },
    },
    {
      key: "bricks", label: "Bricks up", sortable: true, value: (t) => t.bricks_online ?? 0,
      render: (t) => (
        <span className={`text-[12.5px] ${(t.bricks_online ?? 0) < (t.bricks_total ?? 0) ? "text-crit" : ""}`}>
          {/* `bricks` is the ARRAY of bricks, so its length is the fallback total — it was used
              here as if it were a count, which the honest type turned into a compile error
              rather than a run-together render. */}
          {t.bricks_online ?? 0}/{t.bricks_total ?? t.bricks?.length ?? 0}
        </span>
      ),
    },
    {
      // Three states, not two. Not-mounted and not-in-fstab are different failures: an absent
      // mount is invisible to every broken-mount check, and writes to the path land on local disk
      // where no other node can see them and they vanish when the mount returns.
      key: "mounted", label: "Mounted here", sortable: true, value: (t) => (t.mounted ? 1 : 0),
      render: (t) =>
        t.mounted ? (
          <StatusPill tone="ok">mounted</StatusPill>
        ) : t.in_fstab ? (
          <StatusPill tone="bad">declared, not mounted</StatusPill>
        ) : (
          <StatusPill tone="warn">not here</StatusPill>
        ),
    },
    {
      key: "heal", label: "Disagreement", sortable: true, value: (t) => (t.split_brain ?? 0) * 1000 + (t.heal_pending ?? 0),
      render: (t) =>
        (t.split_brain ?? 0) > 0 ? (
          <StatusPill tone="bad">{t.split_brain} split brain</StatusPill>
        ) : (t.heal_pending ?? 0) > 0 ? (
          <StatusPill tone="warn">{t.heal_pending} to heal</StatusPill>
        ) : (
          <span className="text-[12px] text-muted">in step</span>
        ),
    },
    {
      key: "why", label: "", render: (t) => (t.why ? <span className="text-[11.5px] text-muted">{t.why}</span> : null),
    },
  ];

  // `unproven` is NOT one of these. It is a boolean saying some figure on this page could not be
  // measured, and it sat in this list rendering "true" beside five integers — which reads as a
  // count of one thing. It qualifies every number here, so it is shown as a caveat ON the row:
  // a count nobody could verify must not be presented as a count.
  const summary: [string, number | undefined, string][] = [
    ["replicated", d.ha, "ok"],
    ["at risk", d.at_risk, "bad"],
    ["no volume", d.no_volume, "warn"],
    ["not mounted", d.not_mounted, "bad"],
    ["split brain", d.split_brain, "bad"],
  ];

  return (
    <div>
      <Section title="Across the cluster">
        <StateView loading={q.isLoading} error={q.error} errorContext="tenant storage">
          <div className="flex flex-wrap gap-4 text-[13px]">
            {summary.map(([label, n, tone]) => (
              <div key={label} className="min-w-[7rem]">
                <div className={`text-[20px] ${n ? (tone === "bad" ? "text-crit" : tone === "warn" ? "text-warn" : "text-fg") : "text-muted"}`}>
                  {n ?? 0}
                </div>
                <div className="text-[11.5px] text-muted">{label}</div>
              </div>
            ))}
          </div>
          {d.unproven && (
            <div className="mt-3 max-w-3xl rounded border border-warn/40 bg-warn/5 px-3 py-2 text-[12px] leading-relaxed text-warn">
              Some of these figures could not be proven on this pass — a node did not answer, so
              treat the counts above as a floor rather than the whole picture.
            </div>
          )}
          {d.note && <div className="mt-3 max-w-3xl text-[12px] leading-relaxed text-muted">{d.note}</div>}
          {/* WHO MAY MOUNT, and whether that still matches the node registry.
              auth.allow IS the authentication for a Gluster mount — there is no password, so
              being listed here is the credential. CLAUDE.md records the failure directly: a
              tenant volume created before a node joined does not list the joiner's mesh address,
              and that node's own client is then refused with "quorum not met" while its brick is
              online and everything reports healthy. The endpoint that re-syncs it has always
              existed and nothing called it. */}
          {access.data && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface2 px-3 py-2">
              <StatusPill tone={access.data.in_step ? "ok" : "bad"}>
                {access.data.in_step ? "auth.allow in step" : "auth.allow has drifted"}
              </StatusPill>
              <span className="font-mono text-[11.5px] text-muted">{access.data.auth_allow || "unset"}</span>
              {!access.data.in_step && (
                <>
                  <span className="text-[11.5px] text-muted">
                    expected <span className="font-mono">{access.data.expected}</span>
                  </span>
                  <Button
                    size="sm"
                    onClick={() =>
                      setConfirm({
                        title: "Re-sync auth.allow from the node registry?",
                        body: "Sets the allowlist to every registered node's mesh address. A node missing from it cannot mount even though its brick is online — the mount fails as 'quorum not met', which reads like a storage fault rather than an access one.",
                        label: "Re-sync",
                        go: () => client.apiFetch("/glusterfs/access/sync", { method: "POST" }),
                      })
                    }
                  >
                    Re-sync it
                  </Button>
                </>
              )}
            </div>
          )}

          <div className="mt-3 flex gap-2">
            {/* DEGRADED MODE — the two-node incident action, and it was reachable only by shell.
                With one node gone, client quorum stops the survivor serving: the bricks are
                there, the data is intact, and every write fails. Relaxing quorum is what keeps
                the platform up until the peer returns.

                The server REFUSES this while any peer still answers, which is the important
                guard — relaxing quorum with both nodes alive is how a split brain is made — so
                the button can be offered plainly and let the node be the judge of whether it is
                really alone. Turning it back off is DANGER: LOW and is the half people forget,
                so it is offered beside it rather than left to memory. */}
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                setConfirm({
                  title: "Relax client quorum so this node can serve alone?",
                  body: "With a peer gone, quorum stops the survivor serving — the bricks are online, the data is intact, and every write fails anyway. This lets it serve until the peer is back. The node REFUSES this while any peer still answers, because relaxing quorum with both alive is how a split brain starts. Turn it off again once every brick is back.",
                  label: "Relax quorum",
                  go: () => client.apiFetch("/glusterfs/degraded-mode", { method: "POST" }),
                })
              }
            >
              Serve alone (degraded)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setConfirm({
                  title: "Re-enable client quorum?",
                  body: "Restores the normal safety rule now that every brick is back. Do this as soon as the peer has returned — a cluster left in degraded mode has no protection against the split brain quorum exists to prevent.",
                  label: "Re-enable quorum",
                  go: () => client.apiFetch("/glusterfs/degraded-mode", { method: "DELETE" }),
                })
              }
            >
              Restore quorum
            </Button>
            <Button size="sm" variant="ghost"
              onClick={() => setConfirm({
                title: "Extend every tenant volume onto all nodes?",
                body: "Adds a brick on any node that does not hold one, for every tenant volume, and re-syncs auth.allow to each node's mesh address. Adding a brick does not copy what is already there — a heal follows.",
                label: "Extend all",
                go: () => client.apiFetch("/glusterfs/tenants/extend-all", { method: "POST" }),
              })}>
              Extend all onto every node
            </Button>
          </div>
        </StateView>
      </Section>

      <Section title="Tenant homes" className="mt-6" bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={d.items ?? []}
            columns={cols}
            getRowKey={(t) => String(t.volume ?? t.tenant)}
            loading={q.isLoading}
            error={q.error}
            errorContext="tenant storage"
            emptyMessage="No tenant volumes."
            rowActions={(t) => (
              <div className="flex justify-end gap-2">
                {!t.mounted && (
                  <Button size="sm" variant="ghost" onClick={() => void run("Mounted", client.apiFetch(`/glusterfs/tenants/${encodeURIComponent(String(t.tenant))}/mount`, { method: "POST" }))}>
                    Mount
                  </Button>
                )}
                {((t.heal_pending ?? 0) > 0 || (t.split_brain ?? 0) > 0) && (
                  <Button size="sm" variant="ghost" onClick={() => void run("Heal started", client.apiFetch(`/glusterfs/tenants/${encodeURIComponent(String(t.tenant))}/heal`, { method: "POST" }))}>
                    Heal
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setInspecting(String(t.tenant))}>
                  Inspect
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={() => setConfirm({
                    title: `Extend ${t.tenant_name ?? t.tenant} onto every node?`,
                    body: "Adds a brick wherever this volume does not have one and re-syncs auth.allow. The new brick starts EMPTY — adding it does not copy what is already on the volume, so a heal must follow before the copy is real.",
                    label: "Extend",
                    go: () => client.apiFetch(`/glusterfs/tenants/${encodeURIComponent(String(t.tenant))}/extend`, { method: "POST" }),
                  })}>
                  Extend
                </Button>
              </div>
            )}
          />
        </div>
      </Section>

      {inspecting && <TenantVolumeDetail tenant={inspecting} onClose={() => setInspecting(null)} />}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title ?? ""}
        confirmLabel={confirm?.label ?? "Run"}
        body={<p>{confirm?.body}</p>}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c) void run(c.label, c.go());
        }}
      />
    </div>
  );
}

function VolumesTab() {
  const { client } = useAuth();
  const run = useRunner([["gluster-volumes"], ["gluster-heal"]]);
  const q = useQuery({
    queryKey: ["gluster-volumes"],
    queryFn: () => client.apiFetch<{ items: Volume[] | null; total?: number }>("/glusterfs/volumes"),
    retry: false,
  });
  const [open, setOpen] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<{ title: string; body: string; label: string; go: () => Promise<unknown> } | null>(null);

  const cols: DataListColumn<Volume>[] = [
    { key: "name", label: "Volume", sortable: true, render: (v) => <span className="font-mono text-[12.5px]">{v.name}</span> },
    {
      // The distinction that "mounted" hides. A 1×Distribute volume mounts and reads exactly like
      // a replica-2 one and the data exists once.
      key: "shape", label: "Shape", sortable: true, value: (v) => v.replica_count ?? 0,
      render: (v) => (
        <span>
          <span className="text-[12.5px]">{v.type || "?"}</span>
          {(v.replica_count ?? 0) < 2 && <StatusPill tone="bad" >single copy</StatusPill>}
        </span>
      ),
    },
    {
      key: "bricks", label: "Bricks up", sortable: true, value: (v) => v.bricks_online ?? 0,
      render: (v) => (
        <span className={`text-[12.5px] ${(v.bricks_online ?? 0) < (v.brick_count ?? 0) ? "text-crit" : ""}`}>
          {v.bricks_online ?? 0}/{v.brick_count ?? 0}
        </span>
      ),
    },
    {
      key: "started", label: "State", sortable: true, value: (v) => (v.started ? 1 : 0),
      render: (v) => <StatusPill tone={v.started ? "ok" : "bad"}>{v.started ? "started" : v.status || "stopped"}</StatusPill>,
    },
    {
      key: "heal", label: "Disagreement", sortable: true, value: (v) => (v.split_brain ?? 0) * 1000 + (v.heal_pending ?? 0),
      render: (v) =>
        (v.split_brain ?? 0) > 0 ? (
          <StatusPill tone="bad">{v.split_brain} split brain</StatusPill>
        ) : (v.heal_pending ?? 0) > 0 ? (
          <StatusPill tone="warn">{v.heal_pending} to heal</StatusPill>
        ) : (
          <span className="text-[12px] text-muted">in step</span>
        ),
    },
  ];

  return (
    <div>
      <Section title="Volumes" bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={q.data?.items ?? []}
            columns={cols}
            getRowKey={(v) => v.name}
            loading={q.isLoading}
            error={q.error}
            errorContext="the volumes"
            emptyMessage="No volumes on this node."
            rowActions={(v) => (
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === v.name ? null : v.name)}>
                {open === v.name ? "Close" : "Manage"}
              </Button>
            )}
          />
        </div>
      </Section>

      {open && (
        <VolumeDetail
          name={open}
          volume={(q.data?.items ?? []).find((v) => v.name === open)}
          onClose={() => setOpen(null)}
          onAsk={setConfirm}
          run={run}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title ?? ""}
        confirmLabel={confirm?.label ?? "Run"}
        destructive
        body={<p>{confirm?.body}</p>}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c) void run(c.label, c.go());
        }}
      />
    </div>
  );
}

function VolumeDetail({
  name, volume, onClose, onAsk, run,
}: {
  name: string;
  volume?: Volume;
  onClose: () => void;
  onAsk: (c: { title: string; body: string; label: string; go: () => Promise<unknown> }) => void;
  run: (label: string, p: Promise<unknown>) => Promise<unknown>;
}) {
  const { client } = useAuth();
  const heal = useQuery({
    queryKey: ["gluster-heal", name],
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/glusterfs/volumes/${encodeURIComponent(name)}/heal`),
    retry: false,
  });
  const rebalance = useQuery({
    queryKey: ["gluster-rebalance", name],
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/glusterfs/volumes/${encodeURIComponent(name)}/rebalance`),
    retry: false,
  });
  const [brick, setBrick] = React.useState("");
  const [replaceWith, setReplaceWith] = React.useState("");

  return (
    <Section
      className="mt-6"
      title={`${name} — bricks and healing`}
      actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}
    >
      <div className="mb-4">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Bricks</div>
        {(volume?.bricks?.length ?? 0) === 0 ? (
          <div className="text-[13px] text-muted">No brick detail reported.</div>
        ) : (
          <ul className="space-y-0.5 text-[12.5px]">
            {(volume?.bricks ?? []).map((b, i) => (
              <li key={i} className="font-mono">
                <span className={b.online === false ? "text-crit" : ""}>{b.host}:{b.path}</span>
                {b.online === false && <span className="ml-2 font-sans text-[11.5px] text-crit">offline</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void run("Full heal started", client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/heal`, { method: "POST" }))}>
          Heal everything
        </Button>
        {(volume?.split_brain ?? 0) > 0 && (
          <Button size="sm" variant="danger"
            onClick={() => onAsk({
              title: `Resolve split brain on ${name}?`,
              body: "Picks a winning copy for every file the replicas disagree on. The losing version is discarded and cannot be recovered — take a backup of the brick first if the contents matter.",
              label: "Resolve",
              go: () => client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/heal/split-brain`, { method: "POST" }),
            })}>
            Resolve split brain
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => void run("Rebalance started", client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/rebalance`, { method: "POST" }))}>
          Rebalance
        </Button>
        {volume?.started ? (
          <Button size="sm" variant="ghost"
            onClick={() => onAsk({
              title: `Stop ${name}?`,
              body: "Every client mounted on this volume loses access immediately, and writes to the mountpoint land on local disk where no other node can see them.",
              label: "Stop",
              go: () => client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/stop`, { method: "POST" }),
            })}>
            Stop
          </Button>
        ) : (
          <Button size="sm" onClick={() => void run("Volume started", client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/start`, { method: "POST" }))}>
            Start
          </Button>
        )}
      </div>

      {(heal.data || rebalance.data) && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {heal.data && (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Heal</div>
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded border border-line bg-bg p-2 font-mono text-[11px]">
                {JSON.stringify(heal.data, null, 1)}
              </pre>
            </div>
          )}
          {rebalance.data && (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Rebalance</div>
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded border border-line bg-bg p-2 font-mono text-[11px]">
                {JSON.stringify(rebalance.data, null, 1)}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 border-t border-line pt-4">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Add or replace a brick</div>
        <div className="mb-2 max-w-2xl text-[11.5px] leading-relaxed text-muted">
          A brick host must be a real, reachable address — glusterd rejects one that resolves to
          127.x, and it caches its own hostname at startup. A new brick starts EMPTY: adding it does
          not copy what is already on the volume, so a full heal has to follow before the extra copy
          is real.
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={brick}
            onChange={(e) => setBrick(e.target.value)}
            placeholder="node11.ozpanel.net:/data/bricks/oz_ozt100000"
            className="min-w-[18rem] flex-1 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg outline-none focus:border-accent"
          />
          <Button size="sm" disabled={!brick.trim()}
            onClick={() => void run("Brick added", client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/bricks`, {
              // `bricks`, an ARRAY — brickReq is {bricks []string, replica, force, action}. This
              // sent `brick`, a string, so req.Bricks was always empty and the handler answered
              // 400 "at least one brick is required": the button could never add anything.
              method: "POST", body: JSON.stringify({ bricks: [brick.trim()] }),
            }))}>
            Add
          </Button>
          <Button size="sm" variant="ghost" disabled={!brick.trim() || !replaceWith.trim()}
            onClick={() => onAsk({
              title: `Replace ${brick.trim()}?`,
              body: "The new brick is brought into the replica in place of the old one and the data is healed onto it. Use this for a brick whose disk is failing — it keeps the replica count, which removing and re-adding does not.",
              label: "Replace",
              go: () => client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/bricks/replace`, {
                method: "POST", body: JSON.stringify({ old: brick.trim(), new: replaceWith.trim() }),
              }),
            })}>
            Replace with →
          </Button>
          <input
            value={replaceWith}
            onChange={(e) => setReplaceWith(e.target.value)}
            placeholder="the replacement brick"
            className="min-w-[14rem] flex-1 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg outline-none focus:border-accent"
          />
          <Button size="sm" variant="danger" disabled={!brick.trim()}
            onClick={() => onAsk({
              title: `Remove ${brick.trim()}?`,
              body: "Removing a brick drops a copy. If this is the last replica holding the data, the volume is left with one copy and no redundancy — which is the correct repair for a DAMAGED replica, and a data loss risk otherwise.",
              label: "Remove",
              go: () => client.apiFetch(`/glusterfs/volumes/${encodeURIComponent(name)}/bricks`, {
                // Same key, same defect on the removal path. `force` is sent because the case this
                // button exists for is the one CLAUDE.md records — dropping a damaged replica's
                // bricks so the surviving copy can serve — and gluster refuses that without it.
                method: "DELETE", body: JSON.stringify({ bricks: [brick.trim()], force: true }),
              }),
            })}>
            Remove
          </Button>
        </div>
      </div>
    </Section>
  );
}

// One tenant's volume, as GlusterFS itself reports it — the per-brick truth behind the row.
function TenantVolumeDetail({ tenant, onClose }: { tenant: string; onClose: () => void }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["gluster-tenant", tenant],
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/glusterfs/tenants/${encodeURIComponent(tenant)}`),
    retry: false,
  });
  return (
    <Section className="mt-6" title={`Tenant volume — ${tenant}`}
      actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}>
      <StateView loading={q.isLoading} error={q.error} errorContext="this tenant's volume">
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11px]">
          {JSON.stringify(q.data, null, 1)}
        </pre>
      </StateView>
    </Section>
  );
}
